import {
  differenceInCalendarDays,
  differenceInCalendarISOWeeks,
  differenceInCalendarMonths,
  differenceInCalendarQuarters,
  differenceInCalendarYears,
  subDays,
  subWeeks,
  subMonths,
  subQuarters,
  subYears,
  format,
} from 'date-fns'
import { parseDateLocal } from '~/util/date'
import { fiscalYearStart } from '~/util/fiscalYear'
import type { GranularityUnit } from '~/state/useGranularityState'

/** Legibility cap: max time-buckets a governed walk should request (spec §7.1). */
export const MAX_POINTS = 400

// Finest-to-coarsest ordering and an approximate day-count per unit. The counts
// only need to be good enough to choose a legible bucket — exactness is the
// backend's job (it sums into real calendar buckets).
//
// FY is deliberately NOT on this ladder. It is the same DURATION as a calendar year but a
// different PHASE, so APPROX_DAYS cannot tell them apart and ladder POSITION would silently
// decide which one an oversized window lands on. Auto-coarsening must never substitute one for
// the other: coarseningSummary() would then claim "too large for fiscal-year values" about an
// equally-sized bucket, which is false. FY is a user-only terminal choice.
const LADDER: Exclude<GranularityUnit, 'fy'>[] = ['day', 'week', 'month', 'quarter', 'year']
const APPROX_DAYS: Record<GranularityUnit, number> = {
  day: 1,
  week: 7,
  month: 30,
  quarter: 91,
  year: 365,
  fy: 365,
}

export interface GranularityGovernance {
  requestedUnit: GranularityUnit
  effectiveUnit: GranularityUnit
  isCoarsened: boolean
}

/**
 * The finest bucket >= the requested unit whose point count over [from, to] is
 * <= maxPoints. Never finer than requested (coarser-by-choice is honored). Falls
 * back to 'year' for absurd spans (> ~400 years).
 */
export function computeEffectiveBucket(
  fromIso: string,
  toIso: string,
  requestedUnit: GranularityUnit,
  maxPoints: number = MAX_POINTS,
): GranularityUnit {
  // NB: if fromIso > toIso (caller invariant violated), diff is negative and
  // Math.max clamps to 1, so we return the requested unit unchanged.
  const spanDays = Math.max(
    1,
    differenceInCalendarDays(parseDateLocal(toIso), parseDateLocal(fromIso)) + 1,
  )
  // Nothing is coarser than a year, so an FY request is already legible at any span.
  if (requestedUnit === 'fy') return 'fy'
  const startIdx = LADDER.indexOf(requestedUnit)
  if (startIdx === -1) {
    // Was a silent `return requestedUnit` under a now-false "unreachable" comment.
    throw new Error(
      `computeEffectiveBucket: unit is not on the coarsening ladder: ${requestedUnit}`,
    )
  }
  for (let i = startIdx; i < LADDER.length; i++) {
    const unit = LADDER[i]!
    if (Math.ceil(spanDays / APPROX_DAYS[unit]) <= maxPoints) return unit
  }
  return 'year'
}

export function resolveGovernance(
  fromIso: string,
  toIso: string,
  requestedUnit: GranularityUnit,
  maxPoints: number = MAX_POINTS,
): GranularityGovernance {
  const effectiveUnit = computeEffectiveBucket(fromIso, toIso, requestedUnit, maxPoints)
  return { requestedUnit, effectiveUnit, isCoarsened: effectiveUnit !== requestedUnit }
}

/** Inclusive bucket count of [from, to] at `unit` (ISO weeks Mon-based — matches UNIT_TO_BUCKET_FN). */
export function countBuckets(fromIso: string, toIso: string, unit: GranularityUnit): number {
  const from = parseDateLocal(fromIso)
  const to = parseDateLocal(toIso)
  let diff: number
  switch (unit) {
    case 'day':
      diff = differenceInCalendarDays(to, from)
      break
    case 'week':
      diff = differenceInCalendarISOWeeks(to, from)
      break
    case 'month':
      diff = differenceInCalendarMonths(to, from)
      break
    case 'quarter':
      diff = differenceInCalendarQuarters(to, from)
      break
    case 'year':
      diff = differenceInCalendarYears(to, from)
      break
    case 'fy':
      // Count whole fiscal years between the two containing FY starts. Snapping first is what
      // makes this differ from the calendar-year arm on an April-straddling window.
      diff = differenceInCalendarYears(fiscalYearStart(to), fiscalYearStart(from))
      break
    default:
      throw new Error(`countBuckets: unhandled granularity unit ${String(unit satisfies never)}`)
  }
  return Math.max(1, diff + 1)
}

/** Start of the pre-window baseline: fromIso shifted back n buckets at unit. */
export function shiftBackByBuckets(fromIso: string, unit: GranularityUnit, n: number): string {
  const d = parseDateLocal(fromIso)
  let shifted: Date
  switch (unit) {
    case 'day':
      shifted = subDays(d, n)
      break
    case 'week':
      shifted = subWeeks(d, n)
      break
    case 'month':
      shifted = subMonths(d, n)
      break
    case 'quarter':
      shifted = subQuarters(d, n)
      break
    case 'year':
      shifted = subYears(d, n)
      break
    case 'fy':
      // subYears is correct for FY (a fiscal year is exactly one calendar year long) — but it is
      // now stated, not inherited by accident from the old `else` arm.
      shifted = subYears(d, n)
      break
    default:
      throw new Error(
        `shiftBackByBuckets: unhandled granularity unit ${String(unit satisfies never)}`,
      )
  }
  return format(shifted, 'yyyy-MM-dd')
}

export const DEFAULT_GRAN_BLOCKS = 4

/** Row 14: blocks-first derived gran_dots default — v2's getGranularity formula
 *  (MMM-React/src/util/data.js:101): dots = ceil(buckets / 4), clamped to the slider range. */
export function deriveDefaultDots(fromIso: string, toIso: string, unit: GranularityUnit): number {
  return Math.min(
    182,
    Math.max(2, Math.ceil(countBuckets(fromIso, toIso, unit) / DEFAULT_GRAN_BLOCKS)),
  )
}

/** The five units the API accepts as `p_bucketsize`. MUST stay in lockstep with the DB whitelist
 *  (mig 0089:167-168) and with the inline unions at useReviewPageState.ts:61 / walks.ts:37,145. */
export type WireBucket = 'day' | 'week' | 'month' | 'quarter' | 'year'

/** Display unit -> wire bucket. `fy` is folded client-side from monthly rows, so it must never
 *  reach p_bucketsize — the DB raises on it. Exhaustive: a new unit that forgets to declare its
 *  wire form THROWS rather than defaulting to something plausible. */
export function toWireBucket(unit: GranularityUnit): WireBucket {
  switch (unit) {
    case 'fy':
      return 'month'
    case 'day':
    case 'week':
    case 'month':
    case 'quarter':
    case 'year':
      return unit
    default:
      throw new Error(`toWireBucket: unhandled granularity unit ${String(unit satisfies never)}`)
  }
}
